import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { PaymentMethod } from "../domain/paymentDraft";

/**
 * The console's Record payment page (owner decision 2026-09-15).
 *
 *   GET  /billing/{ns}/unpaidinvoices          radbilling GET /invoiceapi/{ns}/unpaid
 *   GET  /billing/{ns}/accounts                radbilling GET /invoiceapi/{ns}/accounts
 *   POST /payment/orderapi/{ns}/{acct}/direct  radpayment RecordDirectPayment
 *
 * All three are superadmin-only at radaccess today (billing:read, payment:*),
 * and scoped by EQUALITY to the caller's namespace. The payment is recorded
 * against the namespace the operator is working in (identity.namespace).
 *
 * 🔑 THE RECEIPT IS NOT ISSUED HERE. radbilling's receipt sweeper issues it for
 * the new paid order within about five minutes and radcomm emails it. Who
 * recorded it comes from the signed-in token at radaccess (X-Caller-Display),
 * never from this request -- there is no field for it.
 */

/** One row of GET /billing/{ns}/unpaidinvoices (radbilling model.UnpaidInvoice). */
export interface UnpaidInvoice {
  id: string;
  accountid: string;
  invoicenumber: string;
  buyername: string;
  currency: string;
  totalamount: number;
  amountpaid: number;
  amountdue: number;
  syncstatus: string;
  createdat: string;
  periodstart: string;
  periodend: string;
}

/** One row of GET /billing/{ns}/accounts (radbilling model.BillingAccount). */
export interface BillingAccount {
  accountid: string;
  /** "" when no name is known; show the account id instead. */
  name: string;
}

/** The order radpayment answers with (model.Order), the fields shown. */
export interface RecordedPayment {
  id: string;
  status: string;
  amount: number;
  paymentmethod?: string;
  connectorreference?: string;
  invoiceid?: string;
  receivedat?: string;
}

export interface RecordPaymentInput {
  accountId: string;
  invoiceId: string | null;
  method: PaymentMethod;
  reference: string;
  amount: number;
  /** RFC 3339 with offset (domain/paymentDraft receivedAtFrom), or null for "now". */
  receivedAt: string | null;
}

const enc = encodeURIComponent;

export async function listUnpaidInvoices(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<UnpaidInvoice[]> {
  const r = await callGateway<UnpaidInvoice[] | null>(
    identity,
    config().gatewayBase,
    `/billing/${enc(ns)}/unpaidinvoices`,
    signal ? { signal } : {},
  );
  return r.data ?? [];
}

export async function listBillingAccounts(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<BillingAccount[]> {
  const r = await callGateway<BillingAccount[] | null>(
    identity,
    config().gatewayBase,
    `/billing/${enc(ns)}/accounts`,
    signal ? { signal } : {},
  );
  return r.data ?? [];
}

/**
 * Record the payment. The field names are radpayment's own
 * (handler/order.go recordDirectPaymentRequest). The UTR is radpayment's
 * idempotency key, so a resend of the same UPI payment records nothing new.
 */
export async function recordPayment(identity: CallerIdentity, ns: string, input: RecordPaymentInput): Promise<RecordedPayment> {
  const body: Record<string, unknown> = {
    method: input.method,
    paymentreference: input.reference.trim(),
    amount: input.amount,
    currency: "INR",
  };
  if (input.invoiceId) body.invoiceid = input.invoiceId;
  if (input.receivedAt) body.receivedat = input.receivedAt;

  const r = await callGateway<RecordedPayment | null>(
    identity,
    config().gatewayBase,
    `/payment/orderapi/${enc(ns)}/${enc(input.accountId)}/direct`,
    { method: "POST", body },
  );
  if (!r.data) throw new Error("radpayment accepted the payment but returned no order; check the receipts before recording it again.");
  return r.data;
}
